import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Phone, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { useT } from "../i18n/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { t } = useT();
  const [params] = useSearchParams();
  const initialType = params.get("type") === "join" ? "join" : "contact";

  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    subject: "",
    message: "",
    type: initialType,
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    setForm((f) => ({ ...f, type: initialType }));
  }, [initialType]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("contact.toastValidation"));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success(t("contact.toastSuccess"));
      setForm({ name: "", email: "", company: "", phone: "", subject: "", message: "", type: form.type });
    } catch (err) {
      console.error(err);
      toast.error(t("contact.toastError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-8">
        <div className="text-xs mono uppercase tracking-[0.3em] text-[#1093f0] mb-4">{t("contact.kicker")}</div>
        <h1 className="text-5xl lg:text-7xl font-semibold text-[#131517] leading-[1.02] max-w-4xl">
          {form.type === "join" ? (
            <>{t("contact.titleB1")} <span className="italic font-normal">{t("contact.titleB2")}</span>.</>
          ) : (
            <>{t("contact.titleA1")} <span className="italic font-normal">{t("contact.titleA2")}</span>.</>
          )}
        </h1>
        <p className="mt-8 text-lg text-[#3f444b] max-w-2xl">
          {t("contact.desc")}
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20">
        <div className="max-w-3xl mx-auto">
          <div className="flex gap-2 mb-8">
              {[
                { id: "contact", label: t("contact.tabContact") },
                { id: "join", label: t("contact.tabJoin") },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setForm({ ...form, type: tab.id })}
                  data-testid={`contact-tab-${tab.id}`}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium border transition-all ${
                    form.type === tab.id
                      ? "bg-[#131517] text-white border-[#131517]"
                      : "bg-white text-[#131517] border-black/10 hover:border-[#131517]"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {sent ? (
              <div
                className="hx-bg-mint border border-[#1093f0]/30 rounded-3xl p-10"
                data-testid="contact-success"
              >
                <CheckCircle2 className="w-10 h-10 text-[#1093f0] mb-4" />
                <h3 className="text-2xl font-semibold mb-2">{t("contact.successTitle")}</h3>
                <p className="text-[#3f444b] mb-6">
                  {t("contact.successDesc")}
                </p>
                <button
                  onClick={() => setSent(false)}
                  className="text-sm font-medium hx-link-underline"
                  data-testid="contact-send-another"
                >
                  {t("contact.sendAnother")}
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Nom complet *" required value={form.name} onChange={update("name")} testid="contact-name" />
                  <Field label="Email *" type="email" required value={form.email} onChange={update("email")} testid="contact-email" />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Entreprise" value={form.company} onChange={update("company")} testid="contact-company" />
                  <Field label="Téléphone" value={form.phone} onChange={update("phone")} testid="contact-phone" />
                </div>
                <Field
                  label={form.type === "join" ? "Poste recherché" : "Sujet"}
                  value={form.subject}
                  onChange={update("subject")}
                  testid="contact-subject"
                />
                <div>
                  <label className="block text-xs uppercase tracking-wider text-[#6b7079] mb-2">
                    {form.type === "join" ? "Présentez-vous *" : "Votre message *"}
                  </label>
                  <textarea
                    rows={6}
                    required
                    value={form.message}
                    onChange={update("message")}
                    data-testid="contact-message"
                    className="w-full bg-transparent border border-black/15 rounded-2xl px-4 py-3.5 text-[#131517] focus:border-[#1093f0] focus:ring-0 outline-none transition-colors resize-none"
                    placeholder={form.type === "join" ? "Parcours, expertise, motivations..." : "Décrivez votre contexte, votre besoin et vos enjeux."}
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="hx-btn-primary inline-flex items-center gap-2 bg-[#131517] text-white px-7 py-4 rounded-full text-sm font-medium disabled:opacity-60"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Envoi...</>
                  ) : (
                    <>Envoyer le message <ArrowRight className="w-4 h-4" /></>
                  )}
                </button>
                <p className="text-xs text-[#6b7079] mt-4">
                  En soumettant ce formulaire, vous acceptez que Hecnos traite vos données
                  pour répondre à votre demande, conformément à notre politique de confidentialité.
                </p>
              </form>
            )}
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, value, onChange, type = "text", required = false, testid }) => (
  <div>
    <label className="block text-xs uppercase tracking-wider text-[#6b7079] mb-2">{label}</label>
    <input
      type={type}
      value={value}
      required={required}
      onChange={onChange}
      data-testid={testid}
      className="w-full bg-transparent border border-black/15 rounded-2xl px-4 py-3.5 text-[#131517] focus:border-[#1093f0] focus:ring-0 outline-none transition-colors"
    />
  </div>
);

const ContactRow = ({ icon: Icon, title, lines }) => (
  <div className="flex gap-4">
    <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
      <Icon className="w-4 h-4 text-[#1093f0]" />
    </div>
    <div>
      <div className="text-xs uppercase tracking-wider text-[#9ca0a6] mb-1.5">{title}</div>
      <div className="space-y-1 text-sm text-[#e4e6e8]">
        {lines.map((l, i) => <div key={i}>{l}</div>)}
      </div>
    </div>
  </div>
);

export default Contact;
